import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "./Settings.css";

export default function Settings() {
  const navigate = useNavigate();

  const admin = JSON.parse(localStorage.getItem("admin"));

  const [settings, setSettings] = useState({
    organization_type: "",
    organization_name: "",
    association_address: "",
    association_email: "",
    association_phone: "",
    association_reference: "",
  });
  const [signatureFile, setSignatureFile] = useState(null);
  const [currentSignature, setCurrentSignature] = useState(null);
  const signatureCanvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  const getCanvasPosition = (event) => {
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDrawingSignature = (event) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    canvas.setPointerCapture?.(event.pointerId);
    const ctx = canvas.getContext("2d");
    const point = getCanvasPosition(event);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
    isDrawingRef.current = true;
    hasDrawnRef.current = true;
  };

  const drawSignature = (event) => {
    if (!isDrawingRef.current || !signatureCanvasRef.current) return;
    event.preventDefault();
    const ctx = signatureCanvasRef.current.getContext("2d");
    const point = getCanvasPosition(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stopDrawingSignature = () => {
    isDrawingRef.current = false;
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
    setSignatureFile(null);
  };

  const useDrawnSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !hasDrawnRef.current) {
      toast.error("Veuillez d'abord signer dans la zone prévue.");
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Impossible de préparer la signature.");
        return;
      }
      setSignatureFile(
        new File([blob], "signature-admin.png", { type: "image/png" }),
      );
      toast.success("Signature prête à être enregistrée.");
    }, "image/png");
  };

  useEffect(() => {
    if (!admin || admin.role !== "super_admin") {
      navigate("/admin");
      return;
    }

    loadSettings();
    loadSignature();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get("/api/getSettings.php");

      setSettings(res.data.settings || res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const saveSettings = async () => {
    try {
      console.log("SENDING:", settings);

      const formData = new FormData();

      Object.keys(settings).forEach((key) => {
        formData.append(key, settings[key] || "");
      });

      if (signatureFile) {
        formData.append("signature", signatureFile);
      }

      const res = await axios.post("/api/saveSettings.php", formData);

      console.log("RESPONSE:", res);
      console.log("DATA:", res.data);

      if (res.data.success) {
        toast.success("Paramètres enregistrés ✅");
      } else {
        toast.error(res.data.error || "Erreur");
      }
    } catch (err) {
      console.error("AXIOS ERROR:", err);

      if (err.response) {
        console.log("STATUS:", err.response.status);
        console.log("DATA:", err.response.data);
      }

      toast.error("Erreur sauvegarde ❌");
    }
  };

  const loadSignature = async () => {
    try {
      const res = await axios.get("/api/getAdminSignature.php", {
        withCredentials: true,
      });

      setCurrentSignature(res.data.signature);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <main className="um-settings-page">
      <div className="um-settings-container">
        <header className="um-settings-header">
          <button
            type="button"
            onClick={() => navigate("/super-admin")}
            className="um-back-button"
          >
            <span aria-hidden="true">←</span> Retour
          </button>
          <div className="um-page-heading">
            <h1>
              <span className="um-heading-gear">⚙️</span> Paramètres{" "}
              <span>Association</span>
            </h1>
            <p>Configuration générale de United Migrants</p>
          </div>
          <div className="um-header-spacer" aria-hidden="true" />
        </header>

        <section className="um-settings-card um-general-card">
          <div className="um-section-heading um-general-heading">
            <div className="um-section-title-group">
              <div className="um-section-icon">▦</div>
              <div>
                <h2>Informations générales</h2>
                <p>
                  Renseignez les informations principales de votre organisation.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={saveSettings}
              className="um-primary-button"
            >
              <span aria-hidden="true">▣</span> Enregistrer les modifications
            </button>
          </div>

          <div className="um-settings-fields">
            {[
              {
                key: "organization_type",
                label: "Type d'organisation",
                icon: "▦",
                type: "text",
              },
              {
                key: "organization_name",
                label: "Nom de l'organisation",
                icon: "♧",
                type: "text",
              },
              {
                key: "association_address",
                label: "Adresse",
                icon: "⌖",
                type: "text",
              },
              {
                key: "association_email",
                label: "Email",
                icon: "✉",
                type: "email",
              },
              {
                key: "association_phone",
                label: "Téléphone",
                icon: "♧",
                type: "tel",
              },
              {
                key: "association_reference",
                label: "Référence",
                icon: "▤",
                type: "text",
              },
            ].map((field) => (
              <div key={field.key} className="um-field">
                <label htmlFor={field.key}>{field.label}</label>
                <div className="um-input-wrap">
                  <span className="um-input-icon" aria-hidden="true">
                    {field.icon}
                  </span>
                  <input
                    id={field.key}
                    type={field.type}
                    value={settings[field.key] || ""}
                    onChange={(e) =>
                      setSettings({ ...settings, [field.key]: e.target.value })
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="um-settings-card um-signature-card">
          <div className="um-section-heading um-signature-heading">
            <div className="um-section-title-group">
              <div className="um-section-icon um-signature-icon">✎</div>
              <div>
                <h2>Signature Administrateur</h2>
                <p>
                  Signature utilisée sur les cartes membres et documents
                  officiels.
                </p>
              </div>
            </div>
          </div>

          <div className="um-signature-layout">
            <div className="um-signature-preview">
              {signatureFile ? (
                <img
                  src={URL.createObjectURL(signatureFile)}
                  alt="Aperçu de la signature sélectionnée"
                />
              ) : currentSignature ? (
                <img
                  src={`/api/image.php?file=${encodeURIComponent(currentSignature)}`}
                  alt="Signature administrateur enregistrée"
                />
              ) : (
                <div className="um-signature-empty">
                  <span>✎</span>
                  <p>Aucune signature enregistrée</p>
                </div>
              )}
            </div>

            <div className="um-signature-tools">
              <div className="um-signature-tools-heading">
                <div className="um-small-orange-icon">✎</div>
                <div>
                  <h3>Dessiner la signature sur l’écran</h3>
                  <p>Signez avec la souris, le doigt ou un stylet.</p>
                </div>
              </div>

              <div className="um-signature-canvas-wrap">
                <canvas
                  ref={signatureCanvasRef}
                  width={900}
                  height={260}
                  aria-label="Zone de signature à dessiner avec le doigt ou la souris"
                  className="um-signature-canvas"
                  style={{ touchAction: "none" }}
                  onPointerDown={startDrawingSignature}
                  onPointerMove={drawSignature}
                  onPointerUp={stopDrawingSignature}
                  onPointerCancel={stopDrawingSignature}
                  onPointerLeave={stopDrawingSignature}
                />
              </div>

              <div className="um-signature-actions">
                <button
                  type="button"
                  onClick={useDrawnSignature}
                  className="um-primary-button um-use-signature"
                >
                  ✎ Utiliser cette signature
                </button>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="um-clear-button"
                >
                  ▤ Effacer la signature
                </button>
              </div>

              <div className="um-or-divider">
                <span />
                OU
                <span />
              </div>

              <div className="um-upload-panel">
                <div className="um-upload-heading">
                  <div className="um-small-orange-icon">⇧</div>
                  <div>
                    <h3>Importer une signature existante</h3>
                    <p>Formats acceptés : PNG, JPG, JPEG (max 5 Mo).</p>
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="um-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error("Le fichier doit faire 5 Mo maximum.");
                      e.target.value = "";
                      return;
                    }
                    if (!["image/png", "image/jpeg"].includes(file.type)) {
                      toast.error("Formats acceptés : PNG, JPG ou JPEG.");
                      e.target.value = "";
                      return;
                    }
                    setSignatureFile(file);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="um-bottom-save">
            <button
              type="button"
              onClick={saveSettings}
              className="um-primary-button"
            >
              ▣ Enregistrer les modifications
            </button>
          </div>
        </section>

        <footer className="um-settings-footer">
          <span>♙ Configuration sécurisée</span>
          <span className="um-footer-separator">|</span>
          <span>Accès réservé aux super administrateurs</span>
        </footer>
      </div>
    </main>
  );
}
